import { ErrorHandler, inject, Injectable, NgZone } from '@angular/core';
import Clarity from '@microsoft/clarity';
import { ApiRequestManager, Authentication } from '@sb/library/api';
import { ApiError, DialogEvents, DialogManager, FormBase, RouterManager, SimpleDialogData } from '@sb/library/base';
import { DialogWarningComponent } from '@sb/library/dialog';

@Injectable()
export class ErrorManager implements ErrorHandler {

    readonly RouterManager = inject(RouterManager);

    private static _handlingApiError = false;

    constructor(
        private _NgZone: NgZone,
        private _DialogManager: DialogManager,
        private _ApiRequestManager: ApiRequestManager
    ) {}

    handleError(error: any) {
        console.error(error);

        if (error instanceof ApiError) {
            if (!ErrorManager._handlingApiError) {
                FormBase.currentForm = null; 
                ErrorManager._handlingApiError = true;
                this._ApiRequestManager.disabled = true;

                if (error.Status === 401) {
                    Authentication.NavigateToLogin();
                } else if (!(window as any)['schoolBoxAppLoaded']) {
                    setTimeout(() => window.location.reload(), 2500);  // Don't just hang on the spinner if API calls failed - try again
                } else if (error.Status === -1) {  // Manually created status representing network issues; see api-request.manager.ts
                    this._NgZone.run(() =>
                        this._DialogManager.CreateDialog(DialogWarningComponent, new SimpleDialogData({
                            Title: $localize`:@@CommonUnstableInternetConnectionTitle:Unstable Internet Connection`,
                            Text: $localize`:@@CommonUnstableInternetDialogText:Please check your network connection and then refresh this page. If the problem persists, contact our support team.`,
                            PrimaryButtonText: $localize`:@@CommonRefreshLabel:Refresh`
                        }), null).events.subscribe(event => {
                            if (event === DialogEvents.PrimaryAction) {
                                window.location.reload();
                            }
                        })
                    );

                    Clarity.upgrade("Error Dialog");
                    Clarity.event("Error.UnstableInternet");
                } else if (error.Status === 403) {
                    let text = $localize`:@@CommonAPIActionNotAllowedDialogText:Sorry, but you are not allowed to do that at this time.`;
                    if (error.Response.ErrorGUID) {
                        text += `<br/><br/><small data-clarity-unmask="true">Error code: ${error.Response.ErrorGUID}</small>`
                    }

                    this._NgZone.run(() =>
                        this._DialogManager.CreateDialog(DialogWarningComponent, new SimpleDialogData({
                            Title: $localize`:@@CommonAPIActionNotAllowedTitle:Action Not Allowed`,
                            Text: text,
                            PrimaryButtonText: $localize`:@@CommonRefreshLabel:Refresh`
                        }), null).events.subscribe(event => {
                            if (event === DialogEvents.PrimaryAction) {
                                this.RouterManager.RefreshedFromErrorDialog();
                            }
                        })
                    );

                    Clarity.upgrade("Error Dialog");
                    Clarity.event("Error.ActionNotAllowed");
                // TODO: code meant for 2nd phase, we will check for 400 in detail pages where
                // if a 400 error comes back when asking for detail from the id on the url. but not for internal id errors.
                //
                // else if (error.Status === 400 && error.Response && error.Response.ErrorCode === ErrorCode.UnparseableID) {
                //     // in the case that input item id is incorrect in the url
                //     // the api call for item details will then return 400 error
                //     // navigate to 404 page, but leave the url unchanged
                //     this._Router.Navigate(PageNotFoundRoutes.PageNotFound, {skipLocationChange: true});
                // }
                // else if (error.Status === 404) {
                //     this._Router.Navigate(PageNotFoundRoutes.PageNotFound, {skipLocationChange: true});
                // }
                } else if (error.Status === 429) {
                    let text = $localize`:@@CommonAPIRateLimitDialogText:Please refresh the page and try again in a few minutes, or contact support if the problem persists.`;
                    if (error.Response.ErrorGUID) {
                        text += `<br/><br/><small data-clarity-unmask="true">Error code: ${error.Response.ErrorGUID}</small>`
                    }

                    this._NgZone.run(() =>
                        this._DialogManager.CreateDialog(DialogWarningComponent, new SimpleDialogData({
                            Title: $localize`:@@CommonAPIRateLimitDialogTitle:Rate Limit Exceeded`,
                            Text: text,
                            PrimaryButtonText: $localize`:@@CommonRefreshLabel:Refresh`
                        }), null).events.subscribe(event => {
                            if (event === DialogEvents.PrimaryAction) {
                                this.RouterManager.RefreshedFromErrorDialog();
                            }
                        })
                    );

                    Clarity.upgrade("Error Dialog");
                    Clarity.event("Error.RateLimit");
                } else {
                    let text = $localize`:@@CommonAPIUnexpectedDialogText:Please refresh the page and try again, or contact support if the problem persists.`;
                    if (error.Response.ErrorGUID) {
                        text += `<br/><br/><small data-clarity-unmask="true">Error code: ${error.Response.ErrorGUID}</small>`
                    }

                    this._NgZone.run(() =>
                        this._DialogManager.CreateDialog(DialogWarningComponent, new SimpleDialogData({
                            Title: $localize`:@@CommonAPIUnexpectedDialogTitle:Sorry, something unexpected occurred.`,
                            Text: text,
                            PrimaryButtonText: $localize`:@@CommonRefreshLabel:Refresh`
                        }), null).events.subscribe(event => {
                            if (event === DialogEvents.PrimaryAction) {
                                this.RouterManager.RefreshedFromErrorDialog();
                            }
                        })
                    );
                    
                    Clarity.upgrade("Error Dialog");
                    Clarity.event("Error.Unexpected");
                }
            }
        }
    }

}
